import ReactDOM from 'react-dom/client'
import type { Root } from 'react-dom/client'
import type { IWidgetConfig, IResolvedConfig } from '../types'
import { Widget, type WidgetControls } from './widget'
import { resolveConfig } from './api-key-validator'

const WIDGET_HOST_ID = 'chat-widget-host'
const OPEN_EVENT = 'chat-widget-open'
const CLOSE_EVENT = 'chat-widget-close'
const TOGGLE_EVENT = 'chat-widget-toggle'

let hostNode: HTMLDivElement | null = null
let shadowContainer: HTMLDivElement | null = null
let root: Root | null = null
let currentConfig: IResolvedConfig | null = null
let controls: WidgetControls | null = null
let eventsAttached = false
let suppressEventHandling = false
let isInitializing = false

function ensureHost() {
    if (hostNode && shadowContainer) {
        return shadowContainer
    }
    
    hostNode = document.getElementById(WIDGET_HOST_ID) as HTMLDivElement | null
    if (!hostNode) {
        hostNode = document.createElement('div')
        hostNode.id = WIDGET_HOST_ID
        document.body.appendChild(hostNode)
    }
    
    const shadowRoot = hostNode.shadowRoot ?? hostNode.attachShadow({ mode: 'open' })
    shadowContainer = document.createElement('div')
    shadowRoot.innerHTML = ''
    shadowRoot.appendChild(shadowContainer)
    
    // Never set aria-hidden="true" on the host - the widget always has accessible elements
    // (toggle button when closed, full UI when open). If aria-hidden was set externally,
    // remove it to prevent conflicts with focused elements inside.
    hostNode.removeAttribute('aria-hidden')
    
    return shadowContainer
}

function attachEventListeners() {
    if (eventsAttached) {
        return
    }
    
    document.addEventListener(OPEN_EVENT, handleOpenEvent)
    document.addEventListener(CLOSE_EVENT, handleCloseEvent)
    document.addEventListener(TOGGLE_EVENT, handleToggleEvent)
    eventsAttached = true
}

function detachEventListeners() {
    if (!eventsAttached) {
        return
    }
    document.removeEventListener(OPEN_EVENT, handleOpenEvent)
    document.removeEventListener(CLOSE_EVENT, handleCloseEvent)
    document.removeEventListener(TOGGLE_EVENT, handleToggleEvent)
    eventsAttached = false
}

function handleOpenEvent() {
    if (suppressEventHandling) return
    controls?.open()
}

function handleCloseEvent() {
    if (suppressEventHandling) return
    controls?.close()
}

function handleToggleEvent() {
    if (suppressEventHandling) return
    controls?.toggle()
}

function dispatchWidgetEvent(type: typeof OPEN_EVENT | typeof CLOSE_EVENT | typeof TOGGLE_EVENT) {
    suppressEventHandling = true
    document.dispatchEvent(new CustomEvent(type))
    suppressEventHandling = false
}

function updateHostAriaHidden() {
    if (hostNode) {
        // Always ensure aria-hidden is removed - the widget always has accessible elements
        // (toggle button when closed, full UI when open). Removing the attribute ensures
        // no conflicts when elements inside receive focus.
        hostNode.removeAttribute('aria-hidden')
    }
}

function mountWidget(config: IResolvedConfig) {
    const container = ensureHost()
    if (!root) {
        root = ReactDOM.createRoot(container)
    }
    
    // Ensure aria-hidden is removed immediately after mounting
    updateHostAriaHidden()
    
    root.render(
        <Widget
        resolvedConfig={config}
        onControlsReady={(ctrls) => {
            controls = ctrls
        }}
        onStateChange={updateHostAriaHidden}
        />,
    )
    attachEventListeners()
    
    // Use MutationObserver to watch for any external changes to aria-hidden
    if (hostNode && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
                    const currentValue = hostNode?.getAttribute('aria-hidden')
                    // If aria-hidden is set to true externally, remove it to prevent conflicts
                    if (currentValue === 'true') {
                        hostNode?.removeAttribute('aria-hidden')
                    }
                }
            })
        })
        
        observer.observe(hostNode, {
            attributes: true,
            attributeFilter: ['aria-hidden'],
        })
    }
}

function unmountWidget() {
    if (root) {
        root.unmount()
    }
    if (hostNode?.parentNode) {
        hostNode.parentNode.removeChild(hostNode)
    }
    root = null
    hostNode = null
    shadowContainer = null
    controls = null
    detachEventListeners()
}

/**
* Initialize the chat widget with just an API key
* 
* @example
* // Minimal initialization - just API key!
* ChatWidget.init({ apiKey: 'cw_abc123...' });
* 
* // With optional visitor identification
* ChatWidget.init({
*   apiKey: 'cw_abc123...',
*   visitorId: 'custom_visitor_id',
*   visitorName: 'John'
* });
* 
* // Note: Customization (theme, position, colors, etc.) is managed through
* // the Chat Widget Dashboard and stored in the database.
*/
async function init(config?: Partial<IWidgetConfig>) {
    if (isInitializing) {
        console.warn('[Chat Widget] Already initializing...')
        return
    }
    
    // Check for global config
    const globalCfg = typeof window !== 'undefined' ? window.ChatConfig ?? {} : {}
    const mergedConfig = { ...globalCfg, ...config } as IWidgetConfig
    
    // Validate API key is provided
    if (!mergedConfig.apiKey) {
        console.error('[Chat Widget] API key is required. Get one from your dashboard.')
        return
    }
    
    isInitializing = true
    
    try {
        console.log('[Chat Widget] Initializing...')
        
        // Resolve configuration (validates API key)
        const resolved = await resolveConfig(mergedConfig)
        
        if (!resolved.isValid) {
            console.error(`[Chat Widget] Initialization failed: ${resolved.error}`)
            isInitializing = false
            return
        }
        
        currentConfig = resolved
        mountWidget(resolved)
        
        console.log('[Chat Widget] ✅ Initialized successfully')
        console.log(`[Chat Widget]    Client: ${resolved.clientId}`)
        console.log(`[Chat Widget]    Visitor: ${resolved.visitorId}`)
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error('[Chat Widget] Initialization error:', errorMessage)
    } finally {
        isInitializing = false
    }
}

function open() {
    if (!controls && currentConfig) {
        mountWidget(currentConfig)
    }
    controls?.open()
    dispatchWidgetEvent(OPEN_EVENT)
}

function close() {
    controls?.close()
    dispatchWidgetEvent(CLOSE_EVENT)
}

function toggle() {
    if (!controls && currentConfig) {
        mountWidget(currentConfig)
    }
    controls?.toggle()
    dispatchWidgetEvent(TOGGLE_EVENT)
}

function destroy() {
    unmountWidget()
    currentConfig = null
}

/**
* Check if widget is initialized
*/
function isReady(): boolean {
    return currentConfig !== null && currentConfig.isValid
}

declare global {
    interface Window {
        ChatWidget?: {
            init: (config?: Partial<IWidgetConfig>) => Promise<void>
            open: () => void
            close: () => void
            toggle: () => void
            destroy: () => void
            isReady: () => boolean
        }
        /** Global config - set before loading script for auto-init */
        ChatConfig?: IWidgetConfig
    }
}

if (typeof window !== 'undefined') {
    window.ChatWidget = {
        init,
        open,
        close,
        toggle,
        destroy,
        isReady,
    }
    
    // Auto-initialize if global config is set
    if (window.ChatConfig?.apiKey) {
        window.ChatWidget.init()
    }
}

export { init, open, close, toggle, destroy, isReady }
